// Conversational AI Configuration Builder
import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Drawer,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
  Psychology as PsychologyIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import conversationService, {
  ConversationMessage,
  ConversationState,
  ConfigPreview
} from '../services/conversationService';

const ConversationalBuilder: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getToken, isLoaded: authLoaded } = useAuth();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  
  // State
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [currentState, setCurrentState] = useState<ConversationState | null>(null);
  const [configPreview, setConfigPreview] = useState<ConfigPreview | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [conversations, setConversations] = useState<any[]>([]);
  const [newConversationDialog, setNewConversationDialog] = useState(false);
  const [switchType, setSwitchType] = useState('edge');
  const [vendor, setVendor] = useState('extreme');
  const [showParameterPanel, setShowParameterPanel] = useState(false);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize connection
  useEffect(() => {
    let isMounted = true;
    
    // Only connect after auth and org are loaded
    if (authLoaded && orgLoaded && isMounted) {
      console.log('Auth and org loaded. Organization:', organization?.id || 'none');
      console.log('Component mounted, initiating connection...');
      connectToConversation();
      loadConversationHistory();
    }
    
    return () => {
      isMounted = false;
      console.log('Component unmounting, disconnecting WebSocket...');
      conversationService.disconnect();
    };
  }, [authLoaded, orgLoaded]);

  // Set up event handlers
  useEffect(() => {
    const unsubscribeMessage = conversationService.onMessage(handleNewMessage);
    const unsubscribeState = conversationService.onStateUpdate(handleStateUpdate);
    const unsubscribePreview = conversationService.onPreviewUpdate(handlePreviewUpdate);
    const unsubscribeConnection = conversationService.onConnectionChange(handleConnectionChange);
    
    return () => {
      unsubscribeMessage();
      unsubscribeState();
      unsubscribePreview();
      unsubscribeConnection();
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectToConversation = async (conversationId?: string) => {
    try {
      setIsLoading(true);
      // Request token - don't specify template, let Clerk use default
      console.log('Current organization:', organization);
      const token = await getToken();
      console.log('Got token for WebSocket:', token ? 'present' : 'missing');
      
      await conversationService.connect(conversationId, token || undefined);
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversationHistory = async () => {
    try {
      // Get fresh token before making API call
      const token = await getToken();
      const data = await conversationService.listConversations(token || undefined);
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const handleNewMessage = (message: ConversationMessage) => {
    setMessages(prev => [...prev, message]);
  };

  const handleStateUpdate = (state: ConversationState) => {
    setCurrentState(state);
  };

  const handlePreviewUpdate = (preview: ConfigPreview | null) => {
    setConfigPreview(preview);
  };

  const handleConnectionChange = (connected: boolean) => {
    setIsConnected(connected);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim() || !isConnected) return;
    
    // Add user message to chat
    const userMessage: ConversationMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Send message via WebSocket
    conversationService.sendMessage(inputMessage);
    
    // Clear input
    setInputMessage('');
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleParameterChange = (param: string, value: any) => {
    conversationService.updateParameter(param, value);
  };

  const handleGenerateConfig = () => {
    conversationService.generateConfiguration();
  };

  const handleSaveConfig = async () => {
    if (!configPreview?.config) return;
    
    try {
      // Save configuration logic here
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save configuration:', error);
    }
  };

  const handleExportConfig = () => {
    if (!configPreview?.config) return;
    
    const blob = new Blob([configPreview.config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentState?.parameters.switch_name || 'config'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyConfig = () => {
    if (!configPreview?.config) return;
    
    navigator.clipboard.writeText(configPreview.config);
    // Could show a snackbar here
  };

  const handleStartNewConversation = () => {
    setNewConversationDialog(true);
  };

  const handleCreateNewConversation = () => {
    setMessages([]);
    setCurrentState(null);
    setConfigPreview(null);
    connectToConversation();
    setNewConversationDialog(false);
  };

  const handleResumeConversation = async (conversationId: string) => {
    setMessages([]);
    await connectToConversation(conversationId);
    setShowHistory(false);
  };

  const renderMessage = (message: ConversationMessage, index: number) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';
    
    return (
      <ListItem key={index} sx={{ flexDirection: isUser ? 'row-reverse' : 'row' }}>
        <ListItemAvatar>
          <Avatar sx={{ 
            bgcolor: isUser ? 'primary.main' : isSystem ? 'grey.500' : 'secondary.main' 
          }}>
            {isUser ? <PersonIcon /> : <AIIcon />}
          </Avatar>
        </ListItemAvatar>
        <Box sx={{ 
          maxWidth: '70%',
          ml: isUser ? 2 : 0,
          mr: isUser ? 0 : 2
        }}>
          <Paper sx={{ 
            p: 2, 
            bgcolor: isUser ? 'primary.light' : 'background.paper',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            wordBreak: 'break-word'
          }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
            </Typography>
            {message.confidence_score && (
              <Chip
                size="small"
                label={`${Math.round(message.confidence_score * 100)}% confident`}
                sx={{ mt: 1 }}
              />
            )}
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {new Date(message.timestamp || '').toLocaleTimeString()}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  const renderParameterPanel = () => (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Configuration Parameters
      </Typography>
      <Divider sx={{ mb: 2 }} />
      
      {currentState?.parameters && Object.entries(currentState.parameters).map(([key, value]) => (
        <Box key={key} sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {key.replace(/_/g, ' ').toUpperCase()}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={value || ''}
            onChange={(e) => handleParameterChange(key, e.target.value)}
            variant="outlined"
          />
        </Box>
      ))}
      
      {currentState?.missing && currentState.missing.length > 0 && (
        <>
          <Typography variant="subtitle2" color="error" sx={{ mt: 2, mb: 1 }}>
            Missing Parameters:
          </Typography>
          {currentState.missing.map(param => (
            <Chip
              key={param}
              label={param.replace(/_/g, ' ')}
              size="small"
              color="error"
              variant="outlined"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </>
      )}
      
      {currentState?.suggestions && currentState.suggestions.length > 0 && (
        <>
          <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
            Suggestions:
          </Typography>
          {currentState.suggestions.map((suggestion, idx) => (
            <Alert key={idx} severity="info" sx={{ mb: 1 }}>
              {suggestion}
            </Alert>
          ))}
        </>
      )}
    </Paper>
  );

  const renderConfigPreview = () => (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Configuration Preview
        </Typography>
        <Box>
          <Tooltip title="Copy to clipboard">
            <IconButton size="small" onClick={handleCopyConfig}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export configuration">
            <IconButton size="small" onClick={handleExportConfig}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Save configuration">
            <IconButton size="small" onClick={handleSaveConfig} color="primary">
              <SaveIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      {configPreview?.validation && (
        <Box sx={{ mb: 2 }}>
          {configPreview.validation.is_valid ? (
            <Alert severity="success" icon={<CheckIcon />}>
              Configuration is valid
            </Alert>
          ) : (
            <Alert severity="error" icon={<ErrorIcon />}>
              Configuration has errors
            </Alert>
          )}
          
          {configPreview.validation.warnings && configPreview.validation.warnings.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1 }}>
              {configPreview.validation.warnings.join(', ')}
            </Alert>
          )}
        </Box>
      )}
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {configPreview?.config ? (
          <SyntaxHighlighter
            language="bash"
            style={vscDarkPlus}
            customStyle={{ 
              margin: 0,
              borderRadius: 4,
              fontSize: '0.875rem'
            }}
          >
            {configPreview.config}
          </SyntaxHighlighter>
        ) : (
          <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
            Configuration will appear here as you provide information
          </Typography>
        )}
      </Box>
      
      {configPreview?.config && (
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleGenerateConfig}
            startIcon={<CodeIcon />}
          >
            Finalize Configuration
          </Button>
        </Box>
      )}
    </Paper>
  );

  return (
    <Container maxWidth={false} sx={{ height: 'calc(100vh - 64px)', py: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, height: '100%' }}>
        {/* Chat Section */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: { xs: '100%', md: '50%', lg: '40%' } }}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  AI Configuration Assistant
                </Typography>
                <Box>
                  <Tooltip title="Conversation history">
                    <IconButton onClick={() => setShowHistory(true)}>
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="New conversation">
                    <IconButton onClick={handleStartNewConversation}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              {isConnected ? (
                <Chip
                  size="small"
                  label="Connected"
                  color="success"
                  icon={<CheckIcon />}
                />
              ) : (
                <Chip
                  size="small"
                  label="Disconnected"
                  color="error"
                  icon={<ErrorIcon />}
                />
              )}
            </Box>
            
            {/* Messages */}
            <List sx={{ 
              flex: 1, 
              overflow: 'auto', 
              p: 2,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                },
              },
            }}>
              {messages.map((message, index) => renderMessage(message, index))}
              {isLoading && (
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <AIIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      AI is thinking...
                    </Typography>
                    <LinearProgress sx={{ mt: 1, width: 100 }} />
                  </Box>
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
            
            {/* Input */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  ref={inputRef}
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={!isConnected}
                />
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!isConnected || !inputMessage.trim()}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Paper>
        </Box>
        
        {/* Parameters Section (Desktop only) */}
        {!isMobile && (
          <Box sx={{ flex: '0 0 auto', width: { md: '25%', lg: '20%' } }}>
            {renderParameterPanel()}
          </Box>
        )}
        
        {/* Configuration Preview */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: { xs: '100%', md: isMobile ? '50%' : '25%', lg: '40%' } }}>
          {renderConfigPreview()}
        </Box>
      </Box>
      
      {/* Mobile Parameter Drawer */}
      {isMobile && (
        <>
          <Fab
            color="primary"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => setShowParameterPanel(true)}
          >
            <SettingsIcon />
          </Fab>
          <Drawer
            anchor="right"
            open={showParameterPanel}
            onClose={() => setShowParameterPanel(false)}
          >
            <Box sx={{ width: 300, p: 2 }}>
              <IconButton onClick={() => setShowParameterPanel(false)} sx={{ mb: 2 }}>
                <CloseIcon />
              </IconButton>
              {renderParameterPanel()}
            </Box>
          </Drawer>
        </>
      )}
      
      {/* Conversation History Drawer */}
      <Drawer
        anchor="left"
        open={showHistory}
        onClose={() => setShowHistory(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Conversation History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {conversations.map((conv) => (
              <ListItem key={conv.id}>
                <ListItemButton onClick={() => handleResumeConversation(conv.id)}>
                  <ListItemText
                    primary={conv.title}
                    secondary={`${conv.status} - ${new Date(conv.updated_at).toLocaleDateString()}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      
      {/* New Conversation Dialog */}
      <Dialog open={newConversationDialog} onClose={() => setNewConversationDialog(false)}>
        <DialogTitle>Start New Conversation</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Switch Type</InputLabel>
            <Select
              value={switchType}
              onChange={(e) => setSwitchType(e.target.value)}
              label="Switch Type"
            >
              <MenuItem value="edge">Edge Switch</MenuItem>
              <MenuItem value="core">Core Switch</MenuItem>
              <MenuItem value="distribution">Distribution Switch</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Vendor</InputLabel>
            <Select
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              label="Vendor"
            >
              <MenuItem value="extreme">Extreme Networks</MenuItem>
              <MenuItem value="cisco">Cisco</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewConversationDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateNewConversation} variant="contained">
            Start Conversation
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConversationalBuilder;